import App from 'next/app'
import React from 'react'
import NProgress from 'nprogress'
import Router from 'next/router'
import { logPageView } from '~/lib/gtm'
import { ApolloProvider, getDataFromTree } from 'react-apollo'
import Head from 'next/head'
import initApollo from '~/lib/apollo/init'
import Layout from '~/components/layout/Layout'
import { QueryProfile } from '~/queries/user.gql'
import { checkScope } from '~/lib/helpers/checkScope'
import Error from '~/pages/_error'
import Title from '~/components/shared/Title'
import sentry from '~/lib/sentry'
import '../styles/index.scss'

const { captureException } = sentry()

// Setup nprogress on router
Router.events.on('routeChangeStart', () => {
  NProgress.configure({
    parent: '#nprogress-parent',
    showSpinner: false
  })
  NProgress.start()
})
Router.events.on('routeChangeComplete', () => NProgress.done())
Router.events.on('routeChangeError', () => NProgress.done())

// Used to init Apollo client, wrap top level pages in layout, and catch and show errors
// It will also get current user data to help determine authorization
// You can pass the following from the page's getInitialProps to effect the layout/permissions
//   - title: Optional string to set title of the page (use components/shared/Title component for dynamic titles), defaults to default title
//   - scope: Optional array or single string of permission names for authorization, shows denied if unauthorized, defaults to empty array for access to anyone
//            Read more at the hasPermission function in `lib/helpers.js`
//   - statusCode: Optional to render error with layout unless 404
//   - mainClassName: Optional string to set custom className on <Main> tag
// You can attach the following to the page to effect layout/auth (like: Component.publicAccess)
//   - publicAccess: Optional to not authenticate user/remove layout elements that need authentication
// Some additional props are passed to the pages for convenience
//   - url: Router info from context { query, pathname, asPath }
//   - profile: The current user profile using QueryProfile query

class MyApp extends App {
  state = {
    statusCode: null,
    message: null
  }

  static async getInitialProps (appContext) {
    const { Component, ctx } = appContext
    let profile = null

    try {
      let appProps = {
        pageProps: {
          url: { query: ctx.query, pathname: ctx.pathname, asPath: ctx.asPath },
          mainClassName: 'pt-3 pb-5'
        },
        authorized: true,
        apolloState: {},
        publicAccess: !!Component.publicAccess
      }
      const apollo = initApollo({ ctx })

      if (!appProps.publicAccess) {
        // Get the user profile
        const { data } = await apollo.query({
          query: QueryProfile,
          variables: {
            id: 'me'
          }
        })

        // Set profile and add profile to pageProps
        profile = data
        appProps.pageProps.profile = profile

        // When redirecting, the response is finished no point in continuing to render
        if (ctx.res && ctx.res.finished) {
          return { statusCode: 401 }
        }

        // If user is not an employee or provider, they don't have access to the site
        if (!checkScope(profile, ['content.employee.view', 'content.provider.view'])) {
          return { statusCode: 403 }
        }
      }

      // Get component initial props
      if (Component.getInitialProps) {
        const componentProps = await App.getInitialProps({ ...appContext, ctx: { ...appContext.ctx, profile, apollo } }) || {}
        appProps = {
          ...appProps,
          ...componentProps,
          pageProps: {
            ...appProps.pageProps,
            ...componentProps.pageProps
          }
        }

        // If statusCode for error then don't show layout unless 404
        if (appProps.pageProps.statusCode && appProps.pageProps.statusCode !== 404) {
          return { statusCode: appProps.pageProps.statusCode, message: appProps.pageProps.message }
        }

        // If scope is passed in from the composed component then run check scope again
        if (!appProps.publicAccess && appProps.pageProps.scope) {
          appProps.authorized = checkScope(profile, appProps.pageProps.scope)
        }
      }

      // Run all GraphQL queries in the component tree and extract the resulting data
      try {
        await getDataFromTree((
          <ApolloProvider client={apollo}>
            <Layout authorized={appProps.authorized} publicAccess={appProps.publicAccess} title={appProps.pageProps.title} url={appProps.pageProps.url} mainClassName={appProps.pageProps.mainClassName}>
              {appProps.authorized && <Component {...appProps.pageProps} apollo={apollo} />}
            </Layout>
          </ApolloProvider>
        ))
      } catch (error) {
        // Handle Apollo errors in components via the data.error prop unless 401
        if (error.networkError && error.networkError.statusCode === 401) {
          throw error
        }
      }

      // getDataFromTree does not call componentWillUnmount head side effect so clear it manually
      if (!process.browser) {
        Head.rewind()
      }

      // When redirecting, the response is finished no point in continuing to render
      if (ctx.res && ctx.res.finished) {
        return { statusCode: 401 }
      }

      // Only include the Apollo data state
      appProps.apolloState.data = apollo.cache.extract()

      return appProps
    } catch (error) {
      if (error.networkError && error.networkError.statusCode === 401) {
        return { statusCode: 401 }
      } else {
        captureException(error, ctx, profile)

        return { statusCode: 500, message: error.message }
      }
    }
  }

  static getDerivedStateFromError (error) {
    if (error.networkError && error.networkError.statusCode === 401) {
      return { statusCode: 401, message: error.message }
    } else {
      return { statusCode: 500, message: error.message }
    }
  }

  componentDidCatch (error, errorInfo) {
    if (!error.networkError || error.networkError.statusCode !== 401) {
      captureException(error, { errorInfo }, this.props.pageProps.profile)
    }
  }

  render () {
    const { Component, pageProps = {}, authorized, publicAccess, statusCode, apolloState, message } = this.props
    const { statusCode: stateStatusCode, message: stateMessage } = this.state
    let result = null

    // Show error if statusCode present or an error has occured, otherwise show component, with apollo, layout etc
    if (statusCode || stateStatusCode) {
      result = <Error statusCode={statusCode || stateStatusCode} message={message || stateMessage} />
    } else {
      // Log page view to Google Analytics
      if (process.browser) {
        logPageView(pageProps.url.asPath)
      }

      // Init apollo from state
      const apollo = apolloState && initApollo({ initialState: apolloState.data })

      result = (
        <ApolloProvider client={apollo}>
          <Layout authorized={authorized} publicAccess={publicAccess} url={pageProps.url} title={pageProps.title} mainClassName={pageProps.mainClassName}>
            {authorized && <Component {...pageProps} apollo={apollo} />}
          </Layout>
        </ApolloProvider>
      )
    }

    return (
      <>
        <Head>
          <meta name='viewport' content='width=device-width, initial-scale=1, maximum-scale=1' />
        </Head>
        <div id='nprogress-parent' className='nprogress' />
        <Title title={pageProps.title} />
        {result}
      </>
    )
  }
}

export default MyApp
